import { forwardRef, useEffect, useRef, type KeyboardEvent, type ReactNode } from 'react';
import MuiIconButton, { type IconButtonProps as MuiIconButtonProps } from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';

/**
 * The few compositions the chrome needs beyond plain MUI: an icon button that is
 * always labelled and tooltipped, a keyboard-shortcut glyph, and the searchable
 * command dialog shared by the launcher and the palette. Everything else uses MUI
 * components directly with the central theme.
 */

type IconButtonProps = Omit<MuiIconButtonProps, 'aria-label'> & {
  /** Required: icon buttons have no visible text. Shown as the tooltip too. */
  readonly label: string;
  readonly shortcut?: string;
};

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ label, shortcut, disabled, ...rest }, ref) => {
    const title = shortcut ? `${label} (${shortcut})` : label;
    // A disabled button does not fire pointer events, so the tooltip needs a wrapper.
    const button = <MuiIconButton ref={ref} aria-label={label} disabled={disabled} {...rest} />;
    return disabled ? (
      <Tooltip title={title}>
        <span style={{ display: 'inline-flex' }}>{button}</span>
      </Tooltip>
    ) : (
      <Tooltip title={title}>{button}</Tooltip>
    );
  },
);
IconButton.displayName = 'IconButton';

export const Kbd = styled('kbd')(({ theme }) => ({
  display: 'inline-block',
  padding: '1px 6px',
  border: `1px solid ${theme.palette.divider}`,
  borderBottomWidth: 2,
  borderRadius: 4,
  color: theme.palette.text.disabled,
  fontFamily: theme.typography.caption.fontFamily,
  fontSize: 10.5,
  lineHeight: 1.4,
  whiteSpace: 'nowrap',
}));

interface CommandDialogProps {
  readonly label: string;
  readonly testId: string;
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  readonly query: string;
  readonly onQueryChange: (value: string) => void;
  readonly placeholder: string;
  readonly inputTestId: string;
  readonly inputLabel: string;
  readonly listId: string;
  readonly activeId: string | undefined;
  readonly prefix?: ReactNode;
  readonly footer: ReactNode;
  readonly children: ReactNode;
  readonly dataAttributes?: Record<`data-${string}`, string | undefined>;
}

/** Modal search-and-pick surface: input on top, listbox below, hints in the footer. */
export const CommandDialog = ({
  label,
  testId,
  open,
  onClose,
  onKeyDown,
  query,
  onQueryChange,
  placeholder,
  inputTestId,
  inputLabel,
  listId,
  activeId,
  prefix,
  footer,
  children,
  dataAttributes,
}: CommandDialogProps) => {
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open, prefix]);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-label={label}
      data-testid={testId}
      maxWidth="sm"
      fullWidth
      disableRestoreFocus={false}
      slotProps={{ paper: { sx: { position: 'absolute', top: '10vh', m: 0, maxHeight: '80vh' }, onKeyDown } }}
      {...dataAttributes}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, px: 3, borderBottom: 1, borderColor: 'divider' }}>
        {prefix}
        <InputBase
          inputRef={inputRef}
          fullWidth
          value={query}
          placeholder={placeholder}
          autoComplete="off"
          spellCheck={false}
          inputProps={{
            'aria-label': inputLabel,
            role: 'combobox',
            'aria-expanded': true,
            'aria-controls': listId,
            'aria-activedescendant': activeId,
            'data-testid': inputTestId,
          }}
          sx={{ py: 2.5, fontSize: 16 }}
          onChange={(e) => {
            onQueryChange(e.target.value);
          }}
        />
      </Box>
      <List id={listId} role="listbox" dense sx={{ p: 2, overflow: 'auto', minHeight: 0 }}>
        {children}
      </List>
      <Typography
        component="footer"
        variant="caption"
        color="text.disabled"
        sx={{ px: 3.5, py: 2, borderTop: 1, borderColor: 'divider' }}
      >
        {footer}
      </Typography>
    </Dialog>
  );
};

export const CommandItem = ListItemButton;

export const EmptyRow = ({ children }: { children: ReactNode }) => (
  <Typography component="li" color="text.disabled" sx={{ px: 2.5, py: 3.5, fontStyle: 'italic' }}>
    {children}
  </Typography>
);
